import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, MapPin } from "lucide-react";
import { scrollToId } from "./Header";

const HERO_IMG =
  "https://images.unsplash.com/photo-1581262177533-1b1760b87952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxwYXJpcyUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZSUyMG1pbmltYWxpc3QlMjBsdXh1cnklMjBidWlsZGluZyUyMG1hcmJsZSUyMHN0b25lfGVufDB8fHx8MTc5MTczNjMzN3ww&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "5", label: "Domaines d'expertise" },
  { value: "100%", label: "Prestations sur-mesure" },
  { value: "TPE · PME", label: "Start-ups, associations & indépendants" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacityImg = useTransform(scrollYProgress, [0, 1], [1, 0.25]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pb-20 pt-32"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 top-[-20%] h-[60vh] w-[60vh] rounded-full bg-gold/10 blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-52 bottom-[-30%] h-[70vh] w-[70vh] rounded-full bg-pine/25 blur-[160px]"
      />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:gap-8 lg:px-12">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-3 border border-ink-line bg-ink-card/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.28em] text-gold"
          >
            <span className="h-1.5 w-1.5 bg-gold" />
            Juriste d'affaires & conformité — Paris 16ᵉ
          </motion.p>

          <h1
            data-testid="hero-heading"
            className="mt-8 font-serif text-5xl font-medium leading-[1.04] tracking-tight text-ivory sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.15}>Conformité,</MaskedLine>
            <MaskedLine delay={0.32}>
              <em className="italic text-gold">sérénité,</em>
            </MaskedLine>
            <MaskedLine delay={0.49}>performance.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-mute sm:text-lg"
          >
            ALMALEX Legal Consulting accompagne les TPE, PME, start-ups,
            associations et entrepreneurs indépendants dans la sécurisation de
            leurs activités et la maîtrise de leurs obligations juridiques et
            réglementaires.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-primary-cta"
              onClick={(e) => scrollToId(e, "#contact")}
              className="group flex items-center gap-3 bg-gold px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-ivory"
            >
              Discuter de votre projet
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#expertises"
              data-testid="hero-secondary-cta"
              onClick={(e) => scrollToId(e, "#expertises")}
              className="group flex items-center gap-3 border border-ink-line px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:border-gold/60 hover:text-gold"
            >
              Découvrir les expertises
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-14 grid grid-cols-1 gap-px border border-ink-line bg-ink-line sm:grid-cols-3"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.label} className="bg-ink px-5 py-5">
                <p className="font-serif text-2xl text-gold">{s.value}</p>
                <p className="mt-1 text-xs leading-snug text-mute">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          style={{ y: yImg, opacity: opacityImg }}
          className="relative lg:col-span-5"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-0 translate-x-4 translate-y-4 border border-gold/40"
            />
            <div className="relative overflow-hidden border border-ink-line">
              <img
                src={HERO_IMG}
                alt="Architecture parisienne — marbre et pierre, élégance minimale"
                data-testid="hero-image"
                className="aspect-[4/5] w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-ink/10" />
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-5 py-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ivory/80">
                  ALMALEX — Paris
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                  Est. 16ᵉ
                </span>
              </div>
            </div>
            <div
              data-testid="hero-location-badge"
              className="absolute -bottom-5 -left-5 flex items-center gap-2.5 border border-ink-line bg-ink-card px-4 py-3 shadow-2xl shadow-black/50"
            >
              <MapPin className="h-4 w-4 text-gold" />
              <span className="text-xs text-ivory/90">
                138 avenue Victor Hugo, 75016 Paris
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 items-center gap-3 lg:flex"
        aria-hidden="true"
      >
        <ArrowDown className="h-4 w-4 animate-bounce text-gold/70" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-mute">
          Défiler
        </span>
      </motion.div>
    </section>
  );
};
